import type { StudioPreviewProduct } from '@repo/types';

/**
 * Substitute the brand's OWN product into the Studio preview's mock certificate
 * (VRG-827).
 *
 * ## Why substitute rather than fetch a real certificate
 *
 * `previewCertificateId` already renders a real record, and Studio deliberately
 * does not pass it — see `previewContext`. That path needs a certificate to EXIST,
 * and Studio is a setup-time surface: a brand tuning its theme routinely has
 * products and no certificates yet, and activation is billable, so "make one to
 * look at it" is not available either. The path would be empty exactly when a
 * brand most needs the preview.
 *
 * So the mock stays the SCAFFOLDING — owner, transfer, scans, status — and only
 * the product is real. That also keeps a theming surface from rendering a real
 * customer's name beside fabricated provenance, which substituting a whole
 * certificate would do.
 *
 * ## Absent means KEEP, never blank
 *
 * A brand's product may have no image, description or SKU. Treating absent as
 * "render nothing" would make the preview get WORSE the sparser the catalogue —
 * an empty image carousel and a blank description tell a brand nothing about
 * their theme. Each field falls back to the mock's, so the preview degrades to
 * exactly today's behaviour field by field rather than all at once.
 *
 * `images: []` is treated as absent for the same reason: a product row with an
 * empty array is a product with no photographs, not a product whose gallery
 * should render empty.
 */
export const applyPreviewProduct = <T extends { product?: unknown }>(
  certificate: T,
  product: StudioPreviewProduct | null | undefined,
): T => {
  if (!product) return certificate;

  const base = (certificate.product ?? {}) as Record<string, unknown>;
  const hasImages = Array.isArray(product.images) && product.images.length > 0;

  return {
    ...certificate,
    product: {
      ...base,
      id: product.id || base.id,
      title: product.title || base.title,
      sku: product.sku || base.sku,
      images: hasImages ? product.images : base.images,
      description: product.description || base.description,
      details: product.details || base.details,
      type: product.type || base.type,
    },
  };
};

/**
 * The list flow (`page=certificates`) renders the whole mock array. Only the FIRST
 * row takes the selected product: the list's job in Studio is to show how a row
 * looks, and rewriting all seven to the same product would turn a varied list into
 * a wall of identical cards — which is a worse preview of a list than the mock is.
 */
export const applyPreviewProductToList = <T extends { product?: unknown }>(
  certificates: T[],
  product: StudioPreviewProduct | null | undefined,
): T[] => {
  if (!product || certificates.length === 0) return certificates;
  return [applyPreviewProduct(certificates[0], product), ...certificates.slice(1)];
};
